import type { HomepageContent } from "./type";

export type RegionCode = "MY" | "TH";

// Call sites build URLs as `${productionUrl}/pages/...`; a trailing slash
// here would double up with that leading slash.
function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

export interface RegionConfig {
  code: RegionCode;
  countryName: string;
  countryId: string;
  apiUrl: string;
  otherRegionApiUrl: string;
  currencyCode: string;
  currencySymbol: string;
  clarityId: string;
  ga4MeasurementId: string;
  whatsappNumber: string;
  defaultPhoneCode: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  homepageHeading: string;
  homepageContent: HomepageContent;
  topLocations: { title: string; url: string }[];
  topCities: { title: string; url: string }[];
  topCountries: { title: string; url: string }[];
  partnerLinks: { href: string; imgSrc: string }[];
  partnerSitemap: { title: string; url: string }[];
  otherRegion: {
    code: RegionCode;
    domain: string;
    flagSvgPath: string;
    label: string;
  };
  domain: string;
  productionUrl: string;
  flagSvgPath: string;
  stripePricingUrl: string;
  protection: {
    basicExcess: number;
  };
  features: {
    enableHomeAnnouncement: boolean;
  };
}

const regionConfigs: Record<RegionCode, RegionConfig> = {
  MY: {
    code: "MY",
    countryName: "Malaysia",
    countryId: "81",
    apiUrl: import.meta.env.VITE_API_URL || process.env.API_URL || "",
    otherRegionApiUrl: import.meta.env.VITE_OTHER_REGION_API_URL || "",
    currencyCode: "MYR",
    currencySymbol: "RM",
    clarityId: "six67s7rw1",
    ga4MeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || "G-X72S1Z5RKE",
    whatsappNumber: import.meta.env.VITE_WHATSAPP_NUMBER || "60192799818",
    defaultPhoneCode: "+60",
    seoTitle: "Europcar Malaysia: Car Rental - Rent a Car Hassle Free",
    seoDescription:
      "Discover exciting offers and book online to save money with our convenient and hassle free booking. Choose from a range of cars. Click to find out more.",
    seoKeywords: "malaysia, car hire, worldwide, car rental",
    homepageHeading: "Car Rental in Malaysia with Europcar",
    homepageContent: {
      leasing: {
        eyebrow: "Corporate Car Leasing Malaysia",
        title: "Leasing a car that easy way",
        description:
          "Everything you need to know about Corporate Car Leasing in Malaysia. Discover all the advantages for your company by leasing with Europcar.",
        buttonText: "More information",
        buttonUrl: "/pages/corporate-car-leasing",
        imageUrl: "/home/ios_app.jpeg",
      },
      fleet: {
        title: "A fleet that meets your needs",
        description: "Take the opportunity to test the new models",
        cards: [
          {
            title: "Sedans",
            description:
              "Explore our range of comfortable and fuel-efficient sedans, perfect for city driving or long-distance travel.",
            imageUrl: "/home/bezza.png",
            url: "/pages/economy-car",
            buttonText: "See more",
          },
          {
            title: "SUVs",
            description:
              "Discover our versatile SUVs with ample space and power, ideal for families and adventurous journeys",
            imageUrl: "/home/x-trail.png",
            url: "/pages/premium-car",
            buttonText: "See more",
          },
          {
            title: "MPVs",
            description:
              "Choose from our selection of spacious MPVs, offering great seating capacity and comfort for group travel.",
            imageUrl: "/home/innova-2024.png",
            url: "/pages/family-car",
            buttonText: "See more",
          },
        ],
      },
    },
    topLocations: [
      { title: "Kuala Lumpur International Airport Terminal 1", url: "/pages/our-locations" },
      { title: "Kuala Lumpur International Airport Terminal 2", url: "/pages/our-locations" },
      { title: "Johor Bahru Senai International Airport", url: "/pages/our-locations" },
      { title: "Penang International Airport", url: "/pages/our-locations" },
    ],
    topCities: [
      { title: "Kuala Lumpur", url: "/pages/our-locations" },
      { title: "Penang", url: "/pages/our-locations" },
      { title: "Johor Bahru", url: "/pages/our-locations" },
      { title: "Kota Kinabalu", url: "/pages/our-locations" },
    ],
    topCountries: [
      { title: "Thailand", url: "/pages/our-locations" },
      { title: "United States of America", url: "/pages/our-locations" },
      { title: "United Kingdom", url: "/pages/our-locations" },
      { title: "France", url: "/pages/our-locations" },
    ],
    partnerLinks: [
      { href: "https://www.europcar.com.my/partners/alliance-partners/hire-a-car-in-china-with-europcar", imgSrc: "partner/china.png" },
      { href: "https://www.europcar.com/partners/routes-car-rental", imgSrc: "partner/routes.png" },
    ],
    partnerSitemap: [
      { title: "Shouqi - China", url: "/pages/partners" },
      { title: "Routes - Canada", url: "/pages/partners" },
      { title: "See all partners", url: "/pages/partners" },
    ],
    otherRegion: {
      code: "TH",
      domain: import.meta.env.VITE_OTHER_REGION_URL || "https://www.europcar.co.th",
      flagSvgPath: "/flags/th.svg",
      label: "TH",
    },
    domain: "europcar.com.my",
    productionUrl: stripTrailingSlash(import.meta.env.VITE_PRODUCTION_URL || process.env.PRODUCTION_URL || ""),
    flagSvgPath: "/flags/my.svg",
    stripePricingUrl: "https://stripe.com/en-my/pricing",
    protection: {
      basicExcess: 6000,
    },
    features: {
      enableHomeAnnouncement: true,
    },
  },
  TH: {
    code: "TH",
    countryName: "Thailand",
    countryId: "127",
    apiUrl: import.meta.env.VITE_API_URL || process.env.API_URL || "",
    otherRegionApiUrl: import.meta.env.VITE_OTHER_REGION_API_URL || "",
    currencyCode: "THB",
    currencySymbol: "฿",
    clarityId: "th-clarity-id",
    ga4MeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID || "G-XXXXXXXXXX",
    whatsappNumber: import.meta.env.VITE_WHATSAPP_NUMBER || "66800000000",
    defaultPhoneCode: "+66",
    seoTitle: "Europcar Thailand: Car Rental - Rent a Car Hassle Free",
    seoDescription:
      "Discover exciting offers and book online to save money with our convenient and hassle free booking. Choose from a range of cars in Thailand.",
    seoKeywords: "thailand, car hire, worldwide, car rental",
    homepageHeading: "Car Rental in Thailand with Europcar",
    homepageContent: {
      leasing: {
        eyebrow: "Corporate Car Leasing Thailand",
        title: "Leasing a car that easy way",
        description:
          "Everything you need to know about Corporate Car Leasing in Thailand. Discover all the advantages for your company by leasing with Europcar.",
        buttonText: "More information",
        buttonUrl: "/pages/corporate-car-leasing",
        imageUrl: "/home/ios_app.jpeg",
      },
      fleet: {
        title: "A fleet that meets your needs",
        description: "Take the opportunity to test the new models",
        cards: [
          {
            title: "Sedans",
            description:
              "Explore our range of comfortable and fuel-efficient sedans, perfect for city driving or long-distance travel.",
            imageUrl: "/home/bezza.png",
            url: "/pages/economy-car",
            buttonText: "See more",
          },
          {
            title: "SUVs",
            description:
              "Discover our versatile SUVs with ample space and power, ideal for families and adventurous journeys",
            imageUrl: "/home/x-trail.png",
            url: "/pages/premium-car",
            buttonText: "See more",
          },
          {
            title: "MPVs",
            description:
              "Choose from our selection of spacious MPVs, offering great seating capacity and comfort for group travel.",
            imageUrl: "/home/innova-2024.png",
            url: "/pages/family-car",
            buttonText: "See more",
          },
        ],
      },
    },
    topLocations: [
      { title: "Suvarnabhumi Airport", url: "/pages/our-locations" },
      { title: "Don Mueang International Airport", url: "/pages/our-locations" },
      { title: "Phuket International Airport", url: "/pages/our-locations" },
      { title: "Chiang Mai International Airport", url: "/pages/our-locations" },
      { title: "Samui Airport", url: "/pages/our-locations" },
    ],
    topCities: [
      { title: "Bangkok", url: "/pages/our-locations" },
      { title: "Phuket", url: "/pages/our-locations" },
      { title: "Chiang Mai", url: "/pages/our-locations" },
      { title: "Krabi", url: "/pages/our-locations" },
      { title: "Koh Samui", url: "/pages/our-locations" },
    ],
    topCountries: [
      { title: "Malaysia", url: "/pages/our-locations" },
      { title: "United States of America", url: "/pages/our-locations" },
      { title: "United Kingdom", url: "/pages/our-locations" },
      { title: "France", url: "/pages/our-locations" },
    ],
    partnerLinks: [
      { href: "https://www.europcar.co.th/partners/alliance-partners/hire-a-car-in-china-with-europcar", imgSrc: "partner/china.png" },
      { href: "https://www.europcar.com/partners/routes-car-rental", imgSrc: "partner/routes.png" },
    ],
    partnerSitemap: [
      { title: "Shouqi - China", url: "/pages/partners" },
      { title: "Routes - Canada", url: "/pages/partners" },
      { title: "See all partners", url: "/pages/partners" },
    ],
    otherRegion: {
      code: "MY",
      domain: import.meta.env.VITE_OTHER_REGION_URL || "https://www.europcar.com.my",
      flagSvgPath: "/flags/my.svg",
      label: "MY",
    },
    domain: "europcar.co.th",
    productionUrl: stripTrailingSlash(import.meta.env.VITE_PRODUCTION_URL || process.env.PRODUCTION_URL || ""),
    flagSvgPath: "/flags/th.svg",
    stripePricingUrl: "https://stripe.com/en-th/pricing",
    protection: {
      basicExcess: 48000,
    },
    features: {
      enableHomeAnnouncement: false,
    },
  },
};

export function getRegionConfig(): RegionConfig {
  const regionCode = (import.meta.env.VITE_REGION || "MY") as RegionCode;
  return regionConfigs[regionCode] || regionConfigs.MY;
}

export function getRegionConfigByCode(code: RegionCode): RegionConfig {
  return regionConfigs[code] || regionConfigs.MY;
}
